import { ReactNode } from 'react';
import { I18n } from 'react-redux-i18n';
import { AutocompleteOption } from '../components/form/autocomplete';
import { countryOptions } from '../constants/countries';
import { pageTypesStrings } from '../constants/pageTypes';

export interface EntityOptionItem {
  id?: any;
  name?: any;
  url?: any;
  urlPath?: any;
}

export interface EntityOptionOverrides {
  /** ID shown on the row, when it differs from the item's id. */
  id?: any;
  /** Context under the name, such as the organisation. The URL then gets its own line. */
  subtext?: ReactNode;
  text?: string;
  url?: string;
  value?: any;
}

/** Autocomplete row for an entity search result: name, optional context, URL and ID. */
export const getEntityOption = (
  item: EntityOptionItem,
  {
    id = item.id,
    subtext,
    text = item.name,
    url = item.url || item.urlPath,
    value = item.id
  }: EntityOptionOverrides = {}
): AutocompleteOption => ({
  text,
  subtext: subtext || url,
  lines: subtext && url ? [url] : undefined,
  aside:
    id === undefined || id === null
      ? undefined
      : [I18n.t('common.autocomplete.id', { id })],
  value
});

export interface PageOptionItem extends EntityOptionItem {
  collectionId?: any;
  fundraiserId?: any;
  teamId?: any;
  type?: string | null;
}

const namedPageTypes: string[] = [
  pageTypesStrings.FUNDRAISER,
  pageTypesStrings.TEAM,
  pageTypesStrings.COLLECTION
];

/** Autocomplete row for a page: name, URL, page type and ID. Its value reads like "team_12". */
export const getPageOption = (item: PageOptionItem): AutocompleteOption => {
  const id = item.fundraiserId || item.teamId || item.collectionId;
  const option = getEntityOption(item, { id, value: `${item.type}_${id}` });
  if (!item.type || !namedPageTypes.includes(item.type)) {
    return option;
  }
  return {
    ...option,
    aside: [
      I18n.t(`common.autocomplete.pageType.${item.type}`),
      ...(option.aside ?? [])
    ]
  };
};

/** Country name for a country code, or the code itself when it is not a known country. */
export const getCountryName = (code?: string | null) =>
  code
    ? (countryOptions.find((x) => x.value === code.toLowerCase())?.text ??
      code.toUpperCase())
    : undefined;

export interface OrganizationOptionItem extends EntityOptionItem {
  address?: { country?: string | null } | null;
  country?: string | null;
}

/** Autocomplete row for an organisation: name, country, URL and ID. */
export const getOrganizationOption = (
  item: OrganizationOptionItem,
  overrides: EntityOptionOverrides = {}
): AutocompleteOption =>
  getEntityOption(item, {
    subtext: getCountryName(item.country ?? item.address?.country),
    ...overrides
  });

/** An organisation's name with its country, when the country is known. */
export const getOrganizationLine = (
  organization?: OrganizationOptionItem | null
) => {
  if (!organization?.name) {
    return undefined;
  }
  const country = getCountryName(
    organization.country ?? organization.address?.country
  );
  return country
    ? I18n.t('common.autocomplete.organisationCountry', {
        organisation: organization.name,
        country
      })
    : organization.name;
};

export interface CampaignOptionItem extends EntityOptionItem {
  organization?: OrganizationOptionItem | null;
  /** Flat organisation fields on lookup results. */
  organizationCountry?: string | null;
  organizationName?: string | null;
}

/** Autocomplete row for a campaign: name, organisation and country, URL and ID. */
export const getCampaignOption = (
  item: CampaignOptionItem,
  overrides: EntityOptionOverrides = {}
): AutocompleteOption =>
  getEntityOption(item, {
    subtext: getOrganizationLine(
      item.organization ?? {
        name: item.organizationName,
        country: item.organizationCountry
      }
    ),
    ...overrides
  });
